import type { Dispatch, SetStateAction } from 'react';
import type { TypedStructure } from '../lib/frameworks';
import './StructureInput.css';

type Area = TypedStructure['areas'][number];

export const emptyArea = (): Area => ({ name: '', indicators: [{ name: '', description: '' }, { name: '', description: '' }] });

/** The areas and indicators a leader types in, without blank rows. */
export function filledStructure(s: TypedStructure): TypedStructure {
  return {
    areas: s.areas
      .map((a) => ({ name: a.name.trim(), indicators: a.indicators.filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), description: i.description.trim() })) }))
      .filter((a) => a.name || a.indicators.length),
  };
}

/**
 * Typing a framework in: areas, each with its indicators and an optional
 * line on what it looks like. Laid out like the admin editor, but lighter.
 */
export function StructureInput({ value, onChange }: { value: TypedStructure; onChange: Dispatch<SetStateAction<TypedStructure>> }) {
  // Every change builds on the latest state, so quick edits (autofill, paste) never overwrite each other.
  const updateArea = (ai: number, fn: (a: Area) => Area) => onChange((prev) => ({ areas: prev.areas.map((a, i) => (i === ai ? fn(a) : a)) }));
  const updateIndicator = (ai: number, ii: number, patch: Partial<Area['indicators'][number]>) =>
    updateArea(ai, (a) => ({ ...a, indicators: a.indicators.map((x, j) => (j === ii ? { ...x, ...patch } : x)) }));
  const removeArea = (ai: number) => onChange((prev) => ({ areas: prev.areas.filter((_, i) => i !== ai) }));

  return (
    <div className="structure">
      {value.areas.map((area, ai) => (
        <fieldset key={ai} className="structure-area">
          <legend className="sr-only">Area {ai + 1}</legend>
          <div className="structure-area-head">
            <input
              id={`area-${ai}`}
              className="structure-area-name"
              placeholder={ai === 0 ? 'Area, e.g. Questioning and checking understanding' : 'Area name'}
              aria-label={`Area ${ai + 1} name`}
              value={area.name}
              onChange={(e) => updateArea(ai, (a) => ({ ...a, name: e.target.value }))}
            />
            {value.areas.length > 1 ? (
              <button type="button" className="structure-remove" onClick={() => removeArea(ai)} aria-label={`Remove area ${ai + 1}`}>
                Remove
              </button>
            ) : null}
          </div>
          <div className="structure-indicators">
            {area.indicators.map((ind, ii) => (
              <div key={ii} className="structure-indicator">
                <div className="structure-indicator-fields">
                  <input
                    id={`ind-${ai}-${ii}`}
                    placeholder={ai === 0 && ii === 0 ? 'Indicator, e.g. Involves all learners' : 'Indicator'}
                    aria-label={`Area ${ai + 1}, indicator ${ii + 1}`}
                    value={ind.name}
                    onChange={(e) => updateIndicator(ai, ii, { name: e.target.value })}
                  />
                  <input
                    id={`desc-${ai}-${ii}`}
                    className="structure-desc"
                    placeholder={ai === 0 && ii === 0 ? 'What it looks like (optional), e.g. Asks named learners, not just volunteers' : 'What it looks like (optional)'}
                    aria-label={`Area ${ai + 1}, indicator ${ii + 1}, what it looks like`}
                    value={ind.description}
                    onChange={(e) => updateIndicator(ai, ii, { description: e.target.value })}
                  />
                </div>
                {area.indicators.length > 1 ? (
                  <button
                    type="button"
                    className="structure-x"
                    onClick={() => updateArea(ai, (a) => ({ ...a, indicators: a.indicators.filter((_, j) => j !== ii) }))}
                    aria-label={`Remove indicator ${ii + 1}`}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            ))}
            {area.indicators.length < 12 ? (
              <button
                type="button"
                className="structure-add"
                onClick={() => updateArea(ai, (a) => ({ ...a, indicators: [...a.indicators, { name: '', description: '' }] }))}
              >
                + Add indicator
              </button>
            ) : null}
          </div>
        </fieldset>
      ))}
      {value.areas.length < 10 ? (
        <button type="button" className="structure-add-area" onClick={() => onChange((prev) => ({ areas: [...prev.areas, emptyArea()] }))}>
          + Add area
        </button>
      ) : null}
    </div>
  );
}
